import { deleteRequest, getRequest, postRequest, putRequest } from 'config/Axios/baseConfig';

import {
  OOCDashboardCounts,
  OOCForecastAdjustment,
  OOCIntegrationSummary,
  OOCNDCSearchResult,
  OOCProvForecastData,
  OOCSubmission,
  OOCTabType,
  OOCValidationResult,
} from 'Interfaces/FDPInterfaces/oocInterfaces';

export const oocService = {
  // ─── Dashboard ──────────────────────────────────────────────

  getSubmissions: async (): Promise<OOCSubmission[]> => {
    const { data } = await getRequest<OOCSubmission[]>('fdp/ooc/submissions');
    return data;
  },

  searchSubmissions: async (query: string, username?: string): Promise<OOCSubmission[]> => {
    const { data } = await getRequest<OOCSubmission[]>('fdp/ooc/submissions/search', undefined, {
      params: { q: query, ...(username ? { username } : {}) },
    });
    return data;
  },

  fetchSuggestions: async (query: string, username: string, signal?: AbortSignal): Promise<string[]> => {
    if (query.trim().length < 2) return [];
    const { data } = await getRequest<string[]>('fdp/ooc/submissions/suggestions', undefined, {
      params: { q: query, username },
      signal,
    });
    return data;
  },

  getDashboardCounts: async (): Promise<OOCDashboardCounts> => {
    const { data } = await getRequest<OOCDashboardCounts>('fdp/ooc/dashboard-counts');
    return data;
  },

  // ─── Single Submission CRUD ─────────────────────────────────

  getSubmissionById: async (id: string): Promise<OOCSubmission | null> => {
    const { data } = await getRequest<OOCSubmission>(`fdp/ooc/submissions/${id}`);
    return data;
  },

  createSubmission: async (submittedBy: string, planningPeriod: string): Promise<OOCSubmission> => {
    const { data } = await postRequest<OOCSubmission>('fdp/ooc/submissions', { submittedBy, planningPeriod });
    return data;
  },

  saveSubmission: async (submission: OOCSubmission): Promise<OOCSubmission> => {
    const { data } = await putRequest<OOCSubmission>(
      `fdp/ooc/submissions/${submission.id}`,
      { reason: submission.reason, adjustments: submission.adjustments },
    );
    return data;
  },

  deleteSubmission: async (id: string): Promise<{ success: boolean }> => {
    const { data } = await deleteRequest<{ success: boolean }>(`fdp/ooc/submissions/${id}`);
    return data;
  },

  // ─── NDC Operations ─────────────────────────────────────────

  searchNDCs: async (query: string, signal?: AbortSignal): Promise<OOCNDCSearchResult[]> => {
    if (!query.trim()) return [];
    const { data } = await postRequest<OOCNDCSearchResult[]>('fdp/ooc_ndc_search', { query }, undefined, { signal });
    return data;
  },

  fetchBaselineValues: async (ndc11s: string[]): Promise<Record<string, { forecast: Record<string, number>; wholesalerDemand: Record<string, number> }>> => {
    const { data } = await postRequest<Record<string, { forecast: Record<string, number>; wholesalerDemand: Record<string, number> }>>(
      'fdp/ooc_baseline_values',
      { ndc11s },
    );
    return data;
  },

  addNDCToSubmission: async (
    submissionId: string,
    ndc: OOCNDCSearchResult,
    targetTab: OOCTabType = 'MAS',
    forecasts: OOCForecastAdjustment['forecasts'] = [],
  ): Promise<OOCForecastAdjustment> => {
    const { data } = await postRequest<OOCForecastAdjustment>(
      `fdp/ooc/submissions/${submissionId}/adjustments`,
      { ndc, tab: targetTab, forecasts },
    );
    return data;
  },

  removeAdjustment: async (submissionId: string, adjustmentId: string): Promise<{ success: boolean }> => {
    const { data } = await deleteRequest<{ success: boolean }>(
      `fdp/ooc/submissions/${submissionId}/adjustments/${adjustmentId}`,
    );
    return data;
  },

  saveAdjustments: async (submissionId: string, adjustments: OOCForecastAdjustment[]): Promise<{ success: boolean }> => {
    const { data } = await putRequest<{ success: boolean }>(
      `fdp/ooc/submissions/${submissionId}/adjustments`,
      { adjustments },
    );
    return data;
  },

  // ─── Provisional Forecast Parameters ────────────────────────

  // Fetches facility rows (MAS) or variance rows (NC) from FCT_ProvisionalForecast
  // + AnnualMarketSales for the given NDC11. `contractType` disambiguates
  // when the NDC has active provisional forecasts of BOTH MAS and NC for
  // the same planning period — omit it only for legacy single-type NDCs.
  fetchProvForecastParams: async (
    ndc11: string,
    contractType?: 'MAS' | 'NC',
  ): Promise<OOCProvForecastData> => {
    const qs = contractType ? `?type=${contractType}` : '';
    const { data } = await getRequest<OOCProvForecastData>(
      `fdp/ooc/prov_forecast_params/${ndc11}${qs}`,
    );
    return data;
  },

  // Saves provisional adjustment facility/variance rows for a submission (draft only).
  saveProvAdjustments: async (
    submissionId: string,
    provAdjustments: Pick<OOCForecastAdjustment, 'id' | 'notes' | 'reason' | 'probability' | 'ncMonthPcts' | 'ncThreshold' | 'facilityRows' | 'varianceRows' | 'selectedPackSizes'>[],
  ): Promise<{ success: boolean }> => {
    const { data } = await putRequest<{ success: boolean }>(
      `fdp/ooc/submissions/${submissionId}/prov_adjustments`,
      { provAdjustments },
    );
    return data;
  },

  // ─── Workflow Actions ───────────────────────────────────────

  communicateToSC: async (id: string): Promise<OOCSubmission> => {
    const { data } = await postRequest<OOCSubmission>(`fdp/ooc/submissions/${id}/communicate`, {});
    return data;
  },

  sendEmail: async (id: string): Promise<{ success: boolean }> => {
    const { data } = await postRequest<{ success: boolean }>(`fdp/ooc/submissions/${id}/send-email`, {});
    return data;
  },

  exportExcel: async (id: string): Promise<Blob> => {
    const { data } = await getRequest<Blob>(`fdp/ooc/submissions/${id}/export-excel`, undefined, { responseType: 'blob' });
    return data;
  },

  markSCReviewing: async (id: string): Promise<OOCSubmission> => {
    const { data } = await postRequest<OOCSubmission>(`fdp/ooc/submissions/${id}/sc-review`, {});
    return data;
  },

  // ─── Validation ─────────────────────────────────────────────

  validateSubmission: async (id: string): Promise<OOCValidationResult> => {
    const { data } = await postRequest<OOCValidationResult>(`fdp/ooc/submissions/${id}/validate`, {});
    return data;
  },

  // ─── History ────────────────────────────────────────────────

  getHistory: async (year: number | null, month?: number, q?: string): Promise<OOCIntegrationSummary[]> => {
    const { data } = await getRequest<OOCIntegrationSummary[]>('fdp/ooc/history', undefined, {
      params: {
        ...(year !== null ? { year } : {}),
        ...(month !== undefined ? { month } : {}),
        ...(q ? { q } : {}),
      },
    });
    return data;
  },

  getHistoricalSubmissionById: async (id: string): Promise<OOCSubmission | null> => {
    const { data } = await getRequest<OOCSubmission>(`fdp/ooc/history/${id}`);
    return data;
  },
};
